import axios from "../../../axios";
import { AppDispatch, RootState } from "../../../redux-store";
import { createAction, createSlice } from "@reduxjs/toolkit";
import * as tPromise from "io-ts-promise";
import * as t from "io-ts";
import { clientProductPricingAgreementCreateUpdateReqBodyT } from "dora-contracts";
import keyBy from "lodash/keyBy";
import {
  createErrorReportingAsyncThunk,
  isFinanceIntegrationTokenInvalidError,
  isFinanceIntegrationUnreachableError,
  unwrap,
} from "../../helpers";
import { notifyL } from "../../notifications";
import { getClients } from "../clients";
import { prettyPrintIfDecodeError } from "../../../helpers/io-ts-helpers";
import {
  ProductsAndSurcharges,
  productsSurchargesAndAgreementsT,
} from "./types";

export interface Product {
  id: string;
  unitId: string | null;
  productNumber: string;
  name: string;
  salesPrice: string | null;
  surcharges: string[];
  shouldBeGroupedInConsolidated: boolean;
  visibleToDrivers: boolean;
  productGroupId: string | null;
}

export type PricingAgreement = {
  id: string;
  clientId: string;
  unitId: string | null;
  unitNumber: number | null;
  productId: string;
  name: string | null;
  salesPrice: string;
  discountPercentage: string | null;
};

type PricingAgreementReqBody = t.TypeOf<
  typeof clientProductPricingAgreementCreateUpdateReqBodyT
>;

export type Surcharge = {
  id: string;
  productNumber: string;
  name: string;
  salesPrice: string | null;
  percentage: string | null;
  isRoadTollSurcharge: boolean;
  shouldBeGroupedInConsolidated: boolean;
  shouldApplyToNewCustomers: boolean;
};

interface State {
  entities: { [key: string]: Product };
  surchargeEntities: { [key: string]: Surcharge };
  surcharges: Surcharge[];
  pricingAgreements: {
    [clientId: string]: PricingAgreement[];
  };
  ids: string[]; // product ids
}

const initialState: State = {
  entities: {},
  surchargeEntities: {},
  surcharges: [],
  ids: [],
  pricingAgreements: {},
};

const prefix = "data/products";

export const loadProducts = createErrorReportingAsyncThunk(
  `${prefix}/loadProducts`,
  async () => {
    const deliveries = await axios.get(`/api/e-conomic/products`);
    return await tPromise
      .decode(productsSurchargesAndAgreementsT, deliveries.data)
      .catch(prettyPrintIfDecodeError);
  }
);

export const convertProductToSurcharge = createErrorReportingAsyncThunk(
  `${prefix}/convertProductToSurcharge`,
  async (product: Product, { dispatch }) => {
    await axios.post(
      `/api/e-conomic/products/${product.id}/convertProductToSurcharge`
    );
    return dispatch(loadProducts());
  }
);

export const convertSurchargeToProduct = createErrorReportingAsyncThunk(
  `${prefix}/convertProductToSurcharge`,
  async (surcharge: Surcharge, { dispatch }) => {
    await axios.post(
      `/api/e-conomic/products/${surcharge.id}/convertSurchargeToProduct`
    );
    return dispatch(loadProducts());
  }
);

export const toggleSurchargeForAllCustomers = createErrorReportingAsyncThunk(
  `${prefix}/applySurchargeToAllCustomers`,
  async (surcharge: Surcharge, { dispatch }) => {
    await axios.post(
      `/api/e-conomic/products/${surcharge.id}/toggle-surcharge-for-all-customers`
    );
    dispatch(loadProducts());
    return dispatch(getClients());
  }
);

export const shouldApplyToNewCustomers = createErrorReportingAsyncThunk(
  `${prefix}/shouldApplyToNewCustomers`,
  async ({ id, value }: { id: string; value: boolean }) => {
    const response = await axios.post(
      `/api/e-conomic/products/${id}/set-should-apply-to-new-customers`,
      { value }
    );
    return response.data;
  }
);

export const toggleRoadTollStatus = createErrorReportingAsyncThunk(
  `${prefix}/toggleRoadTollStatus`,
  async (
    input: { surchargeId: string; isRoadTollSurcharge: boolean },
    { dispatch }
  ) => {
    const { surchargeId, isRoadTollSurcharge } = input;
    await axios.post(`/api/e-conomic/products/${surchargeId}/set-road-toll`, {
      isRoadTollSurcharge,
    });
    dispatch(loadProducts());
  }
);

export const toggleShouldBeGroupedInConsolidated =
  createErrorReportingAsyncThunk(
    `${prefix}/toggleShouldBeGroupedInConsolidated`,
    async (
      { productId, value }: { productId: string; value: boolean },
      { dispatch }
    ) => {
      await axios.post(
        `/api/e-conomic/products/${productId}/set-should-be-grouped-in-consolidated`,
        { shouldBeGroupedInConsolidated: value }
      );
      dispatch(loadProducts());
    }
  );

export const toggleVisibleToDrivers = createErrorReportingAsyncThunk(
  `${prefix}/toggleVisibleToDrivers`,
  async (
    { productId, value }: { productId: string; value: boolean },
    { dispatch }
  ) => {
    await axios.post(
      `/api/e-conomic/products/${productId}/set-visible-to-drivers`,
      {
        visibleToDrivers: value,
      }
    );
    dispatch(loadProducts());
  }
);

export const setVisibleToDriversMultiple = createErrorReportingAsyncThunk(
  `${prefix}/setVisibleToDriversMultiple`,
  async ({ visibleToDrivers }: { visibleToDrivers: boolean }, { dispatch }) => {
    await axios.post(`/api/e-conomic/products/set-visible-to-drivers-multiple`, {
      visibleToDrivers,
    });
    dispatch(loadProducts());
  }
);

const doSetSurchargePercentage = createErrorReportingAsyncThunk(
  `${prefix}/setSurchargePercentage`,
  async ({
    surcharge,
    percentage,
  }: {
    surcharge: Surcharge;
    percentage: string;
  }) => {
    await axios.put(`/api/e-conomic/products/${surcharge.id}/set-percentage`, {
      percentage,
    });
  }
);

const doApplySurchargesForProduct = createErrorReportingAsyncThunk(
  `${prefix}/applySurchargesForProduct`,
  async (
    { product, surchargesIds }: { product: Product; surchargesIds: string[] },
    { dispatch }
  ) => {
    return axios
      .post(
        `/api/e-conomic/products/${product.id}/apply-surcharges-for-product`,
        { surchargesIds }
      )
      .then(() =>
        dispatch(
          notifyL({
            namespace: "notifications",
            key: "surchargeForProductSaved",
            type: "success",
          })
        )
      );
  }
);

export const setSurchargePercentage = unwrap(doSetSurchargePercentage);

export const applySurchargesForProduct = unwrap(doApplySurchargesForProduct);

// Background syncs run on every dispatching page load, so the reconnect toast
// is shown to them once per browser session rather than on each load. Cleared
// on a successful sync so a later invalidation is reported again.
const TOKEN_INVALID_NOTIFIED_KEY = "financeIntegrationTokenInvalidNotified";

const markTokenInvalidNotified = (): boolean => {
  try {
    if (sessionStorage.getItem(TOKEN_INVALID_NOTIFIED_KEY)) {
      return false;
    }
    sessionStorage.setItem(TOKEN_INVALID_NOTIFIED_KEY, "1");
  } catch {
    // Storage unavailable: fall back to notifying.
  }
  return true;
};

const clearTokenInvalidNotified = () => {
  try {
    sessionStorage.removeItem(TOKEN_INVALID_NOTIFIED_KEY);
  } catch {
    // Storage unavailable: nothing to clear.
  }
};

export const syncProducts =
  (options?: { skipErrorNotification?: boolean; force?: boolean }) =>
  async (dispatch: AppDispatch) => {
    let syncedInBackground: boolean;
    try {
      // Without `force` the server skips the sync if one ran recently.
      const response = await axios.post(
        `/api/e-conomic/products/sync${options?.force ? "?force=true" : ""}`
      );
      // 202: the server syncs after replying and pushes the products through
      // the websocket (products-sync-push-websockets).
      syncedInBackground = response.status === 202;
    } catch (err) {
      const isTokenInvalid = isFinanceIntegrationTokenInvalidError(err);
      const isBackground = options?.skipErrorNotification === true;
      // An invalid token needs the user to reconnect, so background syncs that
      // otherwise stay quiet on failure still show it (once per session).
      if (!isBackground || (isTokenInvalid && markTokenInvalidNotified())) {
        dispatch(
          notifyL({
            namespace: "notifications",
            key: isTokenInvalid
              ? "financeIntegrationTokenInvalid"
              : isFinanceIntegrationUnreachableError(err)
                ? "financeIntegrationUnreachable"
                : "productsSyncFailed",
            type: "error",
          })
        );
      }
      throw err;
    }
    clearTokenInvalidNotified();
    await dispatch(loadProducts());
    return { syncedInBackground };
  };

export const syncProductsManual = () => async (dispatch: AppDispatch) => {
  try {
    const { syncedInBackground } = await dispatch(
      syncProducts({ force: true })
    );
    await dispatch(
      notifyL({
        namespace: "notifications",
        key: syncedInBackground ? "productsSyncStarted" : "productsSynced",
        type: syncedInBackground ? "info" : "success",
      })
    );
  } catch {
    // Error notification already dispatched in syncProducts
  }
};

export const createPricingAgreement = createErrorReportingAsyncThunk(
  `${prefix}/create-pricing-agreement`,
  async (pricingAgreement: PricingAgreementReqBody, { dispatch }) => {
    await axios.post(
      `/api/e-conomic/client-product-pricing-agreements`,
      clientProductPricingAgreementCreateUpdateReqBodyT.encode(pricingAgreement)
    );
    return dispatch(loadProducts());
  }
);

export const updatePricingAgreement = createErrorReportingAsyncThunk(
  `${prefix}/update-pricing-agreement`,
  async (
    pricingAgreement: PricingAgreementReqBody & { id: string },
    { dispatch }
  ) => {
    await axios.put(
      `/api/e-conomic/client-product-pricing-agreements/${pricingAgreement.id}`,
      clientProductPricingAgreementCreateUpdateReqBodyT.encode(pricingAgreement)
    );
    return dispatch(loadProducts());
  }
);

export const deletePricingAgreement = createErrorReportingAsyncThunk(
  `${prefix}/delete-pricing-agreement`,
  async (pricingAgreementId: string, { dispatch }) => {
    await axios.delete(
      `/api/e-conomic/client-product-pricing-agreements/${pricingAgreementId}`
    );
    return dispatch(loadProducts());
  }
);

export const productsSynced = createAction<ProductsAndSurcharges>(
  `${prefix}/productsSynced`
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(productsSynced, (state, action) => {
        return {
          ...state,
          surcharges: action.payload.surcharges,
          entities: keyBy(action.payload.products, "id"),
          surchargeEntities: keyBy(action.payload.surcharges, "id"),
          ids: action.payload.products.map((x) => x.id),
        };
      })
      .addCase(loadProducts.fulfilled, (_state, action) => {
        const pricingAgreements: Record<string, PricingAgreement[]> = {};
        for (const pricingAgreement of action.payload.pricingAgreements) {
          if (pricingAgreements[pricingAgreement.clientId]) {
            pricingAgreements[pricingAgreement.clientId].push(pricingAgreement);
          } else {
            pricingAgreements[pricingAgreement.clientId] = [pricingAgreement];
          }
        }
        return {
          ...initialState,
          surcharges: action.payload.surcharges,
          entities: keyBy(action.payload.products, "id"),
          surchargeEntities: keyBy(action.payload.surcharges, "id"),
          ids: action.payload.products.map((x) => x.id),
          pricingAgreements,
        };
      })
      .addCase(shouldApplyToNewCustomers.fulfilled, (state, action) => {
        const surcharge = state.surchargeEntities[action.meta.arg.id];
        if (surcharge) {
          state.surchargeEntities[action.meta.arg.id] = {
            ...surcharge,
            shouldApplyToNewCustomers: action.meta.arg.value,
          };
          state.surcharges = state.surcharges.map((s) =>
            s.id === action.meta.arg.id
              ? { ...s, shouldApplyToNewCustomers: action.meta.arg.value }
              : s
          );
        }
      });
  },
});

export default slice.reducer;
